import { useRef, useState, useCallback } from 'react';
import { useUIScroller } from '../utils/useUIScroller';
import styles from '../styles/ChatSection.module.css';
import { 
  isTextContent, 
  isAudioContent, 
  isFunctionCallContent,
  Message,
  ConversationHistory
} from '../types/history';

interface ChatSectionProps {
  history: ConversationHistory;
  setHistory: (history: ConversationHistory) => void;
  isConnected: boolean;
  currentUserId: string;
  audioRef: React.RefObject<HTMLAudioElement>;
  localCanvasRef: React.RefObject<HTMLCanvasElement>;
  remoteCanvasRef: React.RefObject<HTMLCanvasElement>;
  sendTextMessage: (text: string) => void;
}

export function ChatSection({
  history,
  setHistory,
  isConnected,
  currentUserId,
  audioRef,
  localCanvasRef,
  remoteCanvasRef,
  sendTextMessage
}: ChatSectionProps) {
  const [inputText, setInputText] = useState('');
  const [maximizedSection, setMaximizedSection] = useState<'chat' | null>(null);
  const { scrollRef } = useUIScroller(history.messages);

  const formatTime = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }, []);

  const handleSendMessage = useCallback((text: string) => {
    sendTextMessage(text);
  }, [sendTextMessage]);

  return (
    <div className="section">
      <div className="section-header">
        <div className="section-title">
          <span>CONVERSATION</span>
          <span className="text-sm text-muted">({history.messages.length} MESSAGES)</span>
        </div>
        <div className="button-group">
          <button 
            className="section-maximize"
            onClick={() => setMaximizedSection(maximizedSection === 'chat' ? null : 'chat')}
          >
            {maximizedSection === 'chat' ? '⊟' : '⊞'}
          </button>
        </div>
      </div>
      <div className={styles.messagesSection}>
        <div className={`${styles.chatMessages} ${maximizedSection === 'chat' ? styles.maximized : ''}`}>
          <audio ref={audioRef} autoPlay className="hidden" />

          <div className={styles.waveforms}>
            <canvas ref={localCanvasRef} className={styles.waveform} />
            <canvas ref={remoteCanvasRef} className={styles.waveform} />
          </div>

          <div className={styles.messagesContainer}>
            <div ref={scrollRef} className={styles.eventsList}>
              {history.messages.map((message: Message) => (
                <div 
                  key={message.id} 
                  className={styles.eventLine}
                >
                  <div className={`${styles.eventContent} ${message.role} ${message.status === 'in_progress' ? styles.typing : ''}`}>
                    <div className={styles.eventSourceHeader}>
                      <span className={`${styles.eventSource} ${message.role}`}>
                        {message.role.replace('_', ' ')}
                      </span>
                      <span className={styles.eventTimeInline}>
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                    <div className={styles.eventContentBody}>
                      {message.content && (
                        <>
                          {isTextContent(message.content) && (
                            <div className={styles.textContent}>{message.content.text}</div>
                          )}
                          {isAudioContent(message.content) && (
                            <div className={styles.audioContent}>
                              {message.content.transcript && (
                                <div className={styles.transcript}>
                                  {message.content.transcript}
                                </div>
                              )}
                              {message.content.audio && (
                                <audio 
                                  src={`data:audio/wav;base64,${message.content.audio}`}
                                  controls
                                  className={styles.audioPlayer}
                                />
                              )}
                            </div>
                          )}
                          {isFunctionCallContent(message.content) && (
                            <div className={styles.functionCall}>
                              <div className={styles.functionName}>
                                {message.content.name}({message.content.arguments})
                              </div>
                            </div>
                          )}
                          {message.tool_call_id && isTextContent(message.content) && (
                            <div className={styles.toolResponse}>
                              Tool Response: {message.content.text}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.chatInput}>
            <div className={styles.inputGroup}>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && inputText.trim()) {
                    handleSendMessage(inputText);
                    setInputText('');
                  }
                }}
                placeholder={currentUserId ? "Type your message..." : "Select a user to start..."}
                className={styles.textInput}
                disabled={!isConnected || !currentUserId}
              />
              <button
                onClick={() => {
                  if (inputText.trim()) {
                    handleSendMessage(inputText);
                    setInputText('');
                  }
                }}
                className={styles.btnPrimary}
                disabled={!isConnected || !currentUserId || !inputText.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
